import { z } from "zod";
import { metaSchema } from "./commun.zod";

export const CHAMPIGNON_CATEGORY_VALUES = ["F", "R", "FR"] as const;

export const ChampignonsCategorySchema = z.enum(CHAMPIGNON_CATEGORY_VALUES);

export const ChampignonsBaseSchema = z.object({
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  img_url: z.any().nullable().optional(), // JSON field
  attaque_f: z.string().max(255, "L'attaque F ne peut pas dépasser 255 caractères").nullable().optional(),
  attaque_r: z.string().max(255, "L'attaque R ne peut pas dépasser 255 caractères").nullable().optional(),
  is_deleted: z.boolean().default(false).optional(),
  category: ChampignonsCategorySchema.default("F").nullable().optional(),
});

export const ChampignonsSchema = ChampignonsBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const ChampignonsCreateSchema = ChampignonsBaseSchema.pick({
  name: true,
  attaque_f: true,
  img_url: true,
  attaque_r: true,
  is_deleted: true,
  category: true,
});

export const ChampignonsUpdateSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1, "Le nom est requis").max(255, "Le nom ne peut pas dépasser 255 caractères").nullable().optional(),
  img_url: z.any().nullable().optional(),
  attaque_f: z.string().max(255, "L'attaque F ne peut pas dépasser 255 caractères").nullable().optional(),
  attaque_r: z.string().max(255, "L'attaque R ne peut pas dépasser 255 caractères").nullable().optional(),
  is_deleted: z.boolean().optional(),
  category: ChampignonsCategorySchema.nullable().optional(),
});

export const ChampignonsListResponseSchema = z.object({
  data: z.array(ChampignonsSchema),
  meta: metaSchema
});

export const ChampignonsResponseSchema = z.object({
  data: ChampignonsSchema,
  message: z.string().optional(),
});

export const ChampignonsSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  name: z.string().optional(),
  category: ChampignonsCategorySchema.optional(),
  attaque_f: z.string().optional(),
  attaque_r: z.string().optional(),
  is_deleted: z.boolean().optional(),
  includeDeleted: z.boolean().default(false),
  limit: z.number().default(10),
  page: z.number().default(1),
  search: z.string().optional(),
  sortBy: z.string().optional(),
});

export type Champignons = z.infer<typeof ChampignonsSchema>;
export type ChampignonsCreateInput = z.infer<typeof ChampignonsCreateSchema>;
export type ChampignonsUpdateInput = z.infer<typeof ChampignonsUpdateSchema>;
export type ChampignonsSelectInput = z.infer<typeof ChampignonsSelectSchema>;

// Form-specific types for frontend
export type ChampignonsFormData = Omit<ChampignonsCreateInput, 'is_deleted'> & {
  is_deleted?: boolean;
};

export type ChampignonsEditFormData = Omit<ChampignonsUpdateInput, 'id'>;

// Utility types for different UI states
export type ChampignonsDisplayItem = Pick<Champignons, 'id' | 'name' | 'category' | 'attaque_f' | 'attaque_r'>;
export type ChampignonsSelectOption = Pick<Champignons, 'id' | 'name'>;
export type ChampignonsTableItem = Pick<Champignons, 'id' | 'name' | 'category' | 'attaque_f' | 'attaque_r' | 'is_deleted' | 'created_at' | 'updated_at' | 'img_url'>;

export type ChampignonsResponse = z.infer<typeof ChampignonsResponseSchema>;
export type ChampignonsListResponse = z.infer<typeof ChampignonsListResponseSchema>;

export const validateChampignonsCreate = (data: unknown) => {
  return ChampignonsCreateSchema.safeParse(data);
};

export const validateChampignonsUpdate = (data: unknown) => {
  return ChampignonsUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateChampignonsSelect = (data: unknown) => {
  return ChampignonsSelectSchema.safeParse(data);
};
